import { Base, Component, Prop, Watch } from '@/vue-property-decorator'
import { Bind, Debounce } from '@/utils/Decorator'
import {
  CardDataInterArray,
  CardItemDataInter
} from '../../cardItem/common.type'
import {
  CustomCategoryId,
  GameCategoryIds
} from '@/views/game/consts/categorys'
import { GameItemInter } from '@/service/game/entity/gameItem'
import { GameNavigation } from '@/views/game/classes'
import { JsxHelper } from '@/views/game/utils/jsxHelper'
import { createSvgSprite } from '@/utils/business-utils/assets'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import AppTypes from '@/vue-types'
import style from './style.module.scss'

const NoTitle = -9527

interface Props {
  total: number
  platformName: string
  pageSize: number
  listData: CardDataInterArray
  /** 跳转二级页面，展示title使用 */
  gameCategoryId?: number
  hiddenLoadMore?: boolean // 隐藏查看更多
  fetchData?: (count: number) => Promise<{
    gameList: CardDataInterArray
  }>
}

interface IScopedSlots {
  title?: void
  all?: void
  gameListBox?: {
    /** 此处的真实类型是 GameListType,但它的类型造成了很多与它配合的地方都要强转,所以此处直接就写一个大的类型,用的地方按需强转 */
    data: Array<CardItemDataInter>
  }
  /** 节日动效图 */
  holidayApng?: void
}

@Component<LoadMorePagination>({
  name: 'LoadMorePagination'
})
export default class LoadMorePagination extends Base<
  unknown,
  Props,
  unknown,
  IScopedSlots
> {
  @Prop(AppTypes.number)
  private total!: Props['total']

  @Prop({ default: NoTitle })
  private gameCategoryId!: Props['gameCategoryId']

  @Prop(AppTypes.string)
  private platformName!: Props['platformName']

  // 每一页显示的数量
  @Prop(AppTypes.number.def(0))
  private pageSize!: Props['pageSize']

  @Prop(AppTypes.array.def([]))
  private listData!: Props['listData']

  @Prop(AppTypes.func.def(undefined))
  private fetchData!: Props['fetchData']

  @Prop(AppTypes.looseBool.def(false))
  private hiddenLoadMore!: NonNullable<Props['hiddenLoadMore']>

  // 游戏列表数据
  private gameList: Array<GameItemInter> = []

  // 当前加载到的页面，初始为1
  private current = 1
  private isLoading = false

  mainStore = useMainStore()

  get currentData() {
    let combinedData
    if (this.fetchData) {
      combinedData = [...this.listData, ...this.gameList]
    } else {
      // 折叠分页处理
      combinedData = [...this.listData]
    }
    return combinedData.slice(
      0,
      this.pageSize * this.current
    ) as CardDataInterArray
  }

  /** 是否需要加载更多数据 */
  get loadMoreStatus() {
    return this.total > this.currentData.length && !this.hiddenLoadMore
  }

  @Watch('hiddenLoadMore')
  private resetCurrent(oldV: boolean, newV: boolean) {
    if (!newV && oldV !== newV) {
      this.current = 1
    }
  }

  /** 加载更多数据方法，如果有fetchData则通过其获取，否则直接增加current（折叠分页） */
  private async onLoadMore() {
    if (this.fetchData) {
      this.isLoading = true
      try {
        const { gameList: gameListData } = await this.fetchData(
          this.current + 1
        )
        this.gameList.push(...(gameListData as Array<CardItemDataInter>))
        this.current += 1
      } catch (e) {
        throw new Error('Fetching data failed!')
      } finally {
        this.isLoading = false
      }
    } else {
      this.current += 1
    }
  }

  @Bind()
  @Debounce()
  private onClickLoad() {
    if (!this.isLoading) {
      this.onLoadMore()
    }
  }

  // 监听切换语言 币种切换
  @Watch('mainStore.language', { immediate: true })
  @Watch('mainStore.userInfos.currency', { immediate: true })
  @Watch('mainStore.hasLogined', { immediate: true })
  onStatusChanged() {
    this.gameList = []
    this.current = 1
    this.isLoading = false
  }

  renderTitle() {
    const { t } = useI18n()
    return this.$scopedSlots.title
      ? this.$scopedSlots.title?.()
      : this.gameCategoryId !== NoTitle && (
          <div class={[style.head, 'titleWrap']}>
            <div class={[style.leftTitle, 'leftTitle']}>
              {new JsxHelper().getGameCategoryImg(
                this.gameCategoryId! as GameCategoryIds,
                true
              )}
              {this.mainStore.isCn && t('lobby.game.menue')}
            </div>
            {this.$scopedSlots.holidayApng?.()}
            {this.$scopedSlots.all ? (
              this.$scopedSlots.all?.()
            ) : (
              <div class={[style.rightText, 'rightText']}>
                <div
                  class={style['clickArea']}
                  onClick={() =>
                    GameNavigation.goSubGamePage(
                      this.gameCategoryId!,
                      `${this.gameCategoryId}${CustomCategoryId}`
                    )
                  }
                >
                  {t('lobby.game.loadingAll')}
                </div>
              </div>
            )}
          </div>
        )
  }
  render() {
    const { t } = useI18n()
    const { total, platformName } = this
    return (
      <section class={[style.loadMorePagination, 'load-more-pagination']}>
        {this.renderTitle()}
        {this.$scopedSlots?.gameListBox?.({
          data: this.currentData as Array<CardItemDataInter>
        })}
        {this.loadMoreStatus &&
          (this.isLoading ? (
            <div class={style.scrollDown}>
              {t('lobby.common.tips.loadingTip')}
            </div>
          ) : (
            <div class={[style.scrollDown, 'scroll-down']}>
              {t('lobby.game.loadingInfo', {
                total,
                type: platformName,
                count: this.currentData.length
              })}

              <div
                class={[style.scrollText, 'scroll-text']}
                onClick={this.onClickLoad}
              >
                {t('lobby.game.loadingMore')}
                <icon-sprite sprite={createSvgSprite('comm_icon_jzgd')} />
              </div>
            </div>
          ))}
      </section>
    )
  }
}
